import React, { useRef, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ModalContext } from '$contexts/modal.jsx';
import { useCursorContext } from '$contexts/cursor.jsx';
import { ArrowRightIcon, CloseIcon } from '$partials/icon';
import { Button, Checkbox } from '$partials/ui';

import Util from '$utilities';
import { formatPageTitle, formatPageUri } from '$utilities/format';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').AlertModal}
 */
const RentalPropertyAlertModal = ({
    areaFilters,
    areaFilterOptions,
    fieldGroups,
    maxPrice,
    minPrice,
    hubspot = {},
    page
}) => {
    const filteredAreas = areaFilterOptions?.filter((item) => areaFilters?.includes(item.value));

    const { handleModal } = useContext(ModalContext);
    const { dispatch: cursorDispatch } = useCursorContext();
    const exitButtonRef = useRef(null);
    const hubspotFormId = 'd4b152e9-d735-43be-b41f-75e0296b33a9';

    //need to flatten fieldGroups because areas and pricebrackets are grouped differently to other fields
    const flattenedFields = fieldGroups?.map((group) => group.fields).flat();

    const areas = flattenedFields?.filter((field) => field?.name === 'areas_requested')?.map((item) => item.options).flat() || [];
    const priceBrackets = flattenedFields?.filter((field) => field?.name === 'rental_price_bracket')?.map((item) => item.options).flat() || [];

    // add additional min and max properties as integers
    const priceBracketsWithMinMax = priceBrackets.map((item) => {
        let values;
        if(item.value.includes('-')) {
            values = item.value.split('-');
        } else if(item.value.includes('–')) {
            values = item.value.split('–');
        } else {
            values = item.value;
        }

        const regex = /\D+/gm;
        if(values.length === 2) {
            const min = values[0].replaceAll(regex, '');
            const max = values[1].replaceAll(regex, '');
            return {
                ...item,
                min: parseInt(min),
                max: parseInt(max)
            };
        } else {
            const min = values.replaceAll(regex, '');
            return {
                ...item,
                min: parseInt(min),
            };
        }
    });

    const selectedMinPrice = minPrice || priceBracketsWithMinMax[0]?.min;
    const selectedMaxPrice = maxPrice || priceBracketsWithMinMax.reduce(
        (accumulator, currentValue) => accumulator.min > currentValue.min ? accumulator: currentValue
    )?.min;

    const defaultSelectedAreas = filteredAreas?.map((area) => area.label);
    const defaultSelectedPrices = minPrice || maxPrice ? priceBracketsWithMinMax?.filter((item) =>
        (item.min >= selectedMinPrice && item.min < selectedMaxPrice) ||  (item.max > selectedMinPrice && item.max <= selectedMaxPrice) || (!item.max && !maxPrice && item.min <= selectedMaxPrice)
    )?.map((priceBracket) => priceBracket.value) : [];

    // Form
    const textareaRef = useRef(null);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ height, setHeight ] = useState('');
    const [ message, setMessage ] = useState('');
    const [ hasSubmitted, setHasSubmitted ] = useState(false);

    const {
        formState: { errors },
        handleSubmit,
        register,
        reset,
        watch
    } = useForm({
        defaultValues: {
            areas: defaultSelectedAreas, // must be array of strings for react useform
            priceBrackets: defaultSelectedPrices,
        }
    });
    const { ref, ...rest } = register('address');

    const selectedAreas = watch('areas');
    const selectedPriceBrackets = watch('priceBrackets');

    useEffect(() => {
        const exit = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: exitButtonRef,
                cursorType: 'exit'
            },
        });

        return () => [exit].map(cleanup => {
            cleanup();});
    }, []);

    const submit = async (data) => {
        try {
            const response = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Util.cookies.getItem('crumb')
                },
                body: JSON.stringify({
                    formId: hubspotFormId,
                    fields: [
                        {
                            objectTypeId: '0-1',
                            name: 'firstname',
                            value : data?.firstname
                        },
                        {
                            objectTypeId: '0-1',
                            name: 'lastname',
                            value : data?.lastname
                        },
                        {
                            objectTypeId: '0-1',
                            name: 'email',
                            value : data?.email
                        },
                        {
                            objectTypeId: '0-1',
                            name: 'address',
                            value : data?.address
                        },
                        {
                            objectTypeId: '0-1',
                            name: 'areas_requested',
                            value : data.areas.join(';') // HS needs checkbox values to be semi-colon separated string
                        },
                        {
                            objectTypeId: '0-1',
                            name: 'rental_price_bracket',
                            value : data.priceBrackets.join(';')
                        },
                    ],
                    context:  {
                        hutk: window.sessionStorage.getItem('hubspot_hubspotutk') || undefined,
                        pageUri: formatPageUri(),
                        pageName: formatPageTitle(page)
                    }
                })
            });

            const result = await response.json();

            if (response.status !== 200) {
                if (result && result.message) {
                    setMessage(result.message || 'Thank you for signing up to a property alert');
                    setHasSubmitted(true);
                    return;
                } else {
                    throw new Error(response.status);
                }
            }
            setHasSubmitted(true);
            setMessage(result?.message || 'Thank you for signing up to a property alert');
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setMessage('Sorry, something went wrong, please try again later.');
        }
    };

    const handleInputHeight = () => {
        if (textareaRef.current){
            setHeight(textareaRef.current.scrollHeight);
        }
    };


    return (
        <>
            <div
                className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-black mix-blend-multiply z3 cursor-none'}
                onClick={() => {
                    handleModal();
                    reset();
                }}
                ref={exitButtonRef}
                style={{opacity: '0.2'}}>
            </div>
            <div
                className='height-100 bg-white z5 fixed top-0 overflow-auto modal-container'>
                <button
                    aria-label='Close'
                    className='form-field-reset link-reset absolute right-0 right mt6 mr6 cursor-pointer'
                    onClick={() => {
                        handleModal();
                        reset();
                    }}
                    type='button'>
                    <CloseIcon/>
                </button>
                <div className='py7 px5 md-px6'>
                    <div
                        className='rich-text type-style-2'
                        dangerouslySetInnerHTML={{ __html: hasSubmitted ? message : 'Property Alert'}} />
                    <p className='pt5 pb7 type-style-6'>
                        {hasSubmitted ? '' : 'Enter the details below to add a property alert.'}
                    </p>

                    {hasSubmitted ? (
                        <Button
                            aria-label='Submit'
                            as='button'
                            className = 'bg-black white type-style-5 items-center'
                            look = 'fill'
                            onClick={() => {
                                handleModal();
                                reset();
                            }}
                            renderIcon={<ArrowRightIcon />}>
                                Close
                        </Button>
                    ) : (
                        <form
                            autoComplete='off'
                            data-form-id={hubspotFormId}
                            data-portal-id={hubspot.portalId}
                            onSubmit={handleSubmit(submit)}>
                            <div className='flex flex-wrap mxn5 mb7'>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            First name
                                        </span>
                                        <input
                                            {...register('firstname', { required: 'Please enter your first name.' })}
                                            className={`border-top border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['email']?.message ? ' border-top' : ''}${errors?.['lastname']?.message ? '' : ' border-bottom'}${errors?.['firstname']?.message ? ' border red' : ' black'}`}
                                            id='frm-firstname'/>
                                    </div>
                                    {errors?.['firstname']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['firstname']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            Last name
                                        </span>
                                        <input
                                            {...register('lastname', { required: 'Please enter your last name.' })}
                                            className={`border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['firstname']?.message ? ' border-top' : ''}${errors?.['phone']?.message ? '' : ' border-bottom'}${errors?.['lastname']?.message ? ' border red' : ' black'}`}
                                            id='frm-lastname'/>
                                    </div>
                                    {errors?.['lastname']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['lastname']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5'>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            Email address
                                        </span>
                                        <input
                                            {...register('email', { required: 'Please enter your email.' })}
                                            className={`border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['firstname']?.message ? '' : ' border-bottom'}${errors?.['email']?.message ? ' border red' : ' black'}`}
                                            id='frm-email'/>
                                    </div>
                                    {errors?.['email']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['email']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center relative'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 top-0 pt2 mt5 pointer-events-none'>
                                            Current Address
                                        </span>
                                        <textarea
                                            {...rest}
                                            className={`border-left border-right border-bottom width-100 type-style-3 pt7 pb6 md-py8 pr6 pl5 md-pl6${errors?.['phone']?.message ? ' border-top' : ''}${errors?.['address']?.message ? ' border-top red' : ' black'}`}
                                            id='frm-address'
                                            name='address'
                                            onChange={() => handleInputHeight()}
                                            ref={(e) => {
                                                ref(e);
                                                textareaRef.current = e;
                                            }}
                                            rows={1}
                                            style={{ height: height ? height + 'px' : 'auto'}}/>
                                    </div>
                                    {errors?.['address']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['address']?.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <p className='pt5 pb7 type-style-6'>
                                Alert Preferences
                            </p>
                            <div className='col-12 px5'>
                                {/* Checkboxes */}
                                <div className='flex flex-col mxn5 mb7'>
                                    <div className='col-12 p6 border-top border-left border-right border-bottom'>
                                        <p className='type-style-5'>Locations</p>
                                        <div className='pt4 flex flex-wrap'>
                                            {areas?.map((item) => {
                                                return (
                                                    <div key={item.value}>
                                                        <Checkbox
                                                            checked={selectedAreas.includes(item.value)}
                                                            label={item.label}
                                                            register={register}
                                                            registerName='areas'
                                                            value={item.value} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className='col-12 p6 border-left border-right border-bottom'>
                                        <p className='type-style-5'>Price bracket</p>
                                        <div className='pt4'>
                                            {priceBrackets?.map((item) => {
                                                return (
                                                    <div key={item.value}>
                                                        <Checkbox
                                                            checked={selectedPriceBrackets.includes(item.value)}
                                                            label={item.label}
                                                            register={register}
                                                            registerName='priceBrackets'
                                                            value={item.value} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Button
                                    aria-label='Submit'
                                    as='button'
                                    className = 'bg-black white type-style-5 items-center'
                                    disabled={isLoading}
                                    look = 'fill'
                                    renderIcon={<ArrowRightIcon />}
                                    type='submit'>
                                    Proceed
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default RentalPropertyAlertModal;
